import * as React from 'react';
import { Select, VisuallyHidden } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import type { ConditionalActionValueType } from '@web-config-app/core';
import { CONDITIONAL_ACTION_OPERATORS, applicableOperators } from './operators';
import { OperatorInputs } from './operator-inputs.component';
import type { CatalogOption } from './source-resolution';
import type {
  ConditionalActionCondition,
  OperatorInputs as OperatorInputsType,
} from './types';

interface ConditionOperatorValueProps {
  condition: ConditionalActionCondition;
  /**
   * Logical value type of the active subject — drives which operators
   * apply. Resolved by the caller off the item that owns the subject.
   */
  valueType: ConditionalActionValueType | undefined;
  /**
   * Comparison-value options for the active subject, pre-resolved by the
   * caller off the same item the `valueType` came from.
   */
  catalogOptions: CatalogOption[];
  /**
   * Optional allow-list of operator IDs offered by this schema instance.
   * `undefined` = no restriction. A stored operator outside the allow-list
   * still renders so the author can see and change it.
   */
  allowedOperators: string[] | undefined;
  /** Optional per-instance operator label-key overrides, keyed by operator ID. */
  operatorLabelOverrides: Record<string, string> | undefined;
  /**
   * Full entity edit path of THIS condition — base for the rendered
   * fields' unique `id`/`name`s (a11y/autofill) and the value picker's
   * scoped-data-source anchor.
   */
  conditionEditPath: string;
  enabled: boolean;
  /** Replace this condition with `next` (operator and/or inputs changed). */
  onUpdate: (next: ConditionalActionCondition) => void;
}

/**
 * The operator `<select>` + comparison-value picker for one condition —
 * the part of a condition row that is identical across the branching and
 * visibility controls (operator applicability, label overrides, value
 * seeding/input). Each control owns the *subject* (a fixed pill, a source
 * `<select>`, or a referenced-question picker) and the row's
 * layout/connectors, and composes this in.
 */
export const ConditionOperatorValue: React.FC<ConditionOperatorValueProps> = ({
  condition,
  valueType,
  catalogOptions,
  allowedOperators,
  operatorLabelOverrides,
  conditionEditPath,
  enabled,
  onUpdate,
}) => {
  const { formatMessage } = useIntl();

  const operatorsForSubject = React.useMemo(() => {
    // Catalog-backed = options actually resolve for THIS item. A choice
    // question has answer options, so the equality/set operators apply.
    const applicable = applicableOperators(
      valueType,
      catalogOptions.length > 0,
      condition.subjectType,
    );
    if (!allowedOperators) return applicable;
    const filtered = applicable.filter((op) =>
      allowedOperators.includes(op.id),
    );
    // Keep an out-of-allow-list STORED operator visible so the author can
    // see what the condition currently does and switch away from it —
    // hiding it would make the select silently show the placeholder
    // while the data still holds the old operator.
    const stored =
      condition.operatorId &&
      !filtered.some((op) => op.id === condition.operatorId)
        ? applicable.filter((op) => op.id === condition.operatorId)
        : [];
    return [...filtered, ...stored];
  }, [
    valueType,
    catalogOptions,
    allowedOperators,
    condition.operatorId,
    condition.subjectType,
  ]);

  const operatorLabel = (op: { id: string; label: string }) =>
    operatorLabelOverrides?.[op.id] ?? op.label;

  const activeOperator = condition.operatorId
    ? CONDITIONAL_ACTION_OPERATORS.find((op) => op.id === condition.operatorId)
    : undefined;

  return (
    <>
      <VisuallyHidden as="label" htmlFor={`${conditionEditPath}.operatorId`}>
        {formatMessage({ id: 'CONDITIONAL_ACTION_OPERATOR_LABEL' })}
      </VisuallyHidden>
      <Select
        id={`${conditionEditPath}.operatorId`}
        name={`${conditionEditPath}.operatorId`}
        value={condition.operatorId ?? ''}
        onChange={(evt) => {
          const nextOp = CONDITIONAL_ACTION_OPERATORS.find(
            (op) => op.id === evt.currentTarget.value,
          );
          if (!nextOp) return;
          // Keep the author's values when the input shape is unchanged; reseed
          // defaults when coming from no operator or switching shape (e.g.
          // set → scalar), since the old inputs don't fit the new picker.
          const keepInputs =
            activeOperator && activeOperator.inputShape === nextOp.inputShape;
          onUpdate({
            ...condition,
            operatorId: nextOp.id,
            inputs: keepInputs ? condition.inputs : nextOp.defaultInputs(),
          });
        }}
        disabled={!enabled}
        options={[
          {
            value: '',
            label: formatMessage({
              id: 'CONDITIONAL_ACTION_OPERATOR_PLACEHOLDER',
            }),
          },
          ...operatorsForSubject.map((op) => ({
            value: op.id,
            label: formatMessage({ id: operatorLabel(op) }),
          })),
        ]}
      />
      {activeOperator && (
        <OperatorInputs
          inputs={condition.inputs}
          onChange={(next: OperatorInputsType) =>
            onUpdate({ ...condition, inputs: next })
          }
          options={catalogOptions}
          editPath={conditionEditPath}
          enabled={enabled}
          inputShape={activeOperator.inputShape}
        />
      )}
    </>
  );
};
